import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Download, Copy, AlertCircle, X, ChevronRight, Loader2
} from 'lucide-react';
import {
  getCpcStatsForPage, getCpcStatsBulk,
  CpcStatsResponse, CpcCompetitor
} from '../api/pins';
import { getClicksByPage } from '../api/feed';
import { ClicksByPageResponse, ClicksByPageModelRow } from '../api/types';
import PageHeader from '../components/shared/PageHeader';
import SubTabs from '../components/shared/SubTabs';
import { SkelKpi, SkelRow } from '../components/shared/Skeleton';
import pathPagesData from '../../path_pages.json';

const CPC_TABS = [
  { label: 'Overview', to: '/cpc/overview'         },
  { label: 'By page',  to: '/cpc',         end: true },
  { label: 'OF vs OT',  to: '/cpc/of-ot' }
];

const ALL_PAGES: string[][] = (pathPagesData as { pages: string[][] }).pages;

const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat('en-US').format(n) : '—';
const fmtCpc = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(4)}` : '—';
const fmtCpcShort = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(3)}` : '—';
const fmtPct = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';

const CLICK_RANGES = [
  { value: 1,  label: '1d' },
  { value: 3,  label: '3d' },
  { value: 7,  label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' }
] as const;

// pageKey для click_events.by-page: tags соединены дефисом — совпадает
// с тем, как Nuxt useClickTracking формирует ?p= из /best/onlyfans-{slug}.
// Special-case: пустой tags == "Best" (homepage), Nuxt пишет pageKey="best".
// /best — special page driven by the synthetic "best" tag (V11 migration
// assigns it to every model). So picking /best means tags=['best'], not [].
function isBestOnly(tags: string[]): boolean {
  return tags.length === 1 && tags[0] === 'best';
}

function tagsToPageKey(tags: string[]): string {
  return tags.length === 0 ? 'best' : tags.join('-');
}

function pageSlug(tags: string[]): string {
  return tags.length === 0 ? 'best' : tags.join('+');
}
function pageDisplay(tags: string[]): string {
  if (tags.length === 0 || isBestOnly(tags)) return '/best';
  return tags.length === 1 ? `/${tags[0]}` : `/${tags.join(' + ')}`;
}
function pageLiveUrl(tags: string[], domain = 'onlyseeker.io'): string {
  if (tags.length === 0 || isBestOnly(tags)) return `https://${domain}/best`;
  const slug = tags.join('-');
  return `https://${domain}/best/onlyfans-${slug}`;
}

const CpcByPagePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pageQuery, setPageQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState<string[] | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // Collapsed picker (one-line summary) once a page is chosen.
  // Click "Change" sets pickerEditing=true to re-expand the search UI.
  const [pickerEditing, setPickerEditing] = useState(false);

  // Поддержка drilldown из ClicksByPageOverviewPage:
  // /cpc?pageKey=big-tits → ищем точное совпадение в ALL_PAGES (поиск
  // по slug join('-')) и автоселектим. Если pageKey собран с другим
  // порядком тегов — fallback на substring matching через query.
  useEffect(() => {
    const pk = searchParams.get('pageKey');
    if (!pk || selectedPage) return;
    // pageKey="best" → homepage (tags=[]) — repository handles empty list.
    if (pk === 'best') {
      setSelectedPage(['best']);
      setPageQuery('best');
      return;
    }
    const exact = ALL_PAGES.find(p => p.join('-') === pk);
    if (exact) {
      setSelectedPage(exact);
      setPageQuery(exact.join('+'));
    } else {
      setPageQuery(pk);  // фронт-юзер видит и может выбрать руками
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Lead calc
  const [offeredCpc, setOfferedCpc] = useState<string>('0.05');
  const [dailyBudget, setDailyBudget] = useState<string>('50');

  // Tracking v2 — диапазон для аналитики кликов
  const [clicksDays, setClicksDays] = useState<number>(30);

  // Bulk export
  const [bulkBusy, setBulkBusy] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter pages by query. Best (homepage) — special entry с tags=[]
  // в начале списка; matches при пустой query или поисковых терминах
  // "best"/"home".
  // /best is the V11 "best" tag page — all models carry it, so it acts as
  // the global PPC ladder. Inject it as a synthetic top entry; path_pages
  // doesn't include it because it's a special tag.
  const filteredPages = useMemo<string[][]>(() => {
    const BEST: string[] = ['best'];
    const q = pageQuery.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    if (!q) {
      return [BEST, ...ALL_PAGES.slice(0, 49)];
    }
    const terms = q.split(/[\s,+\-/]+/).filter(Boolean);
    const bestMatches = terms.every(t => 'best'.includes(t));
    const matched = ALL_PAGES.filter(p => {
      const slug = p.join('-');
      const flat = p.join(' ');
      return terms.every(t => slug.includes(t) || flat.includes(t));
    }).slice(0, bestMatches ? 49 : 50);
    return bestMatches ? [BEST, ...matched] : matched;
  }, [pageQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch CPC stats for selected page
  const { data: cpc, isLoading: cpcLoading, isError: cpcError, refetch } = useQuery<CpcStatsResponse>({
    queryKey: ['cpc-stats', selectedPage],
    queryFn: () => getCpcStatsForPage(selectedPage!),
    enabled: !!selectedPage
  });

  // Параллельно — реальные клики из tracking v2 для этой же страницы.
  // Использует выбранный clicksDays — показывает в UI page-traffic блоке.
  const pageKey = selectedPage ? tagsToPageKey(selectedPage) : null;
  const { data: clicks } = useQuery<ClicksByPageResponse>({
    queryKey: ['clicks-by-page', pageKey, clicksDays],
    queryFn: () => getClicksByPage(pageKey!, clicksDays, { monetization: 'PPC', tiers: 'tier1,tier2' }),
    enabled: !!pageKey
  });

  // Отдельный fetch специально для forecast — всегда last 24h, чтобы
  // прогноз отражал актуальное состояние ladder'а, а не агрегат за месяц.
  const { data: clicks24h } = useQuery<ClicksByPageResponse>({
    queryKey: ['clicks-by-page-24h', pageKey],
    queryFn: () => getClicksByPage(pageKey!, 1, { monetization: 'PPC', tiers: 'tier1,tier2' }),
    enabled: !!pageKey
  });

  // Lookup модель → клики по username (CpcCompetitor не имеет modelId).
  const clicksByUsername = useMemo(() => {
    const m = new Map<string, ClicksByPageModelRow>();
    if (clicks) {
      for (const row of clicks.byModel) {
        m.set(row.username.toLowerCase(), row);
      }
    }
    return m;
  }, [clicks]);

  /**
   * Ladder-churn detector: если за окно данных в click_events были модели,
   * которых сейчас нет в ladder'е, значит состав менялся → forecast по
   * tier/position становится менее точным.
   *
   *  - missingModels: были в кликах, но ушли из ladder.
   *  - newModels   : сейчас в ladder, но ещё не накопили кликов за окно.
   *  - level       : "high" если >30% разницы, "low" иначе.
   *
   * Без ladder-snapshots это эвристика, но даёт партнёру понимание что
   * forecast в волатильной странице может отставать от реальности.
   */
  const ladderChurn = useMemo(() => {
    if (!cpc || !clicks) return null;
    const currentUsernames = new Set(cpc.models.map(m => m.username.toLowerCase()));
    const historicUsernames = new Set(clicks.byModel.map(m => m.username.toLowerCase()));
    const missing = [...historicUsernames].filter(u => !currentUsernames.has(u));
    const newOnes = [...currentUsernames].filter(u => !historicUsernames.has(u));
    const totalSeen = currentUsernames.size + missing.length;
    const churnRatio = totalSeen > 0 ? (missing.length + newOnes.length) / totalSeen : 0;
    return {
      missingCount: missing.length,
      newCount: newOnes.length,
      missingTop: missing.slice(0, 5),
      ratio: churnRatio,
      level: churnRatio > 0.3 ? 'high' : churnRatio > 0.1 ? 'low' : 'none'
    };
  }, [cpc, clicks]);

  /**
   * Tier-level аналитика: для каждой группы моделей с одинаковым CPC —
   * среднее и медианное число кликов в день за период.
   *
   * Группируем cpc.models по pricePerClick (полный ladder, включая модели
   * без кликов — иначе средние занижены). Источник кликов — clicksByUsername;
   * отсутствие записи трактуется как 0. Делим на clicks.days чтобы получить
   * кликов/день вместо суммарных за период.
   */
  type TierAgg = {
    avgClicksPerDay: number;
    medianClicksPerDay: number;
    stdevClicksPerDay: number | null;
    modelCount: number;
  };
  const tierAggregates = useMemo(() => {
    if (!cpc || !clicks) return new Map<number, TierAgg>();
    const groups = new Map<number, number[]>();
    for (const m of cpc.models) {
      if (m.pricePerClick == null) continue;
      const row = clicksByUsername.get(m.username.toLowerCase());
      const total = row?.total ?? 0;
      const list = groups.get(m.pricePerClick) ?? [];
      list.push(total);
      groups.set(m.pricePerClick, list);
    }
    const out = new Map<number, TierAgg>();
    for (const [cpcVal, totals] of groups.entries()) {
      const days = clicks.days || 30;
      const perDay = totals.map(t => t / days);
      const n = perDay.length;
      const avg = perDay.reduce((s, v) => s + v, 0) / n;
      const sorted = [...perDay].sort((a, b) => a - b);
      const mid = Math.floor(n / 2);
      const median = n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      // Sample stdev (n-1 в знаменателе), null если n<2
      let stdev: number | null = null;
      if (n > 1) {
        const variance = perDay.reduce((s, v) => s + (v - avg) ** 2, 0) / (n - 1);
        stdev = Math.sqrt(variance);
      }
      out.set(cpcVal, { avgClicksPerDay: avg, medianClicksPerDay: median, stdevClicksPerDay: stdev, modelCount: n });
    }
    return out;
  }, [cpc, clicks, clicksByUsername]);

  // Lead calculator
  const offered = parseFloat(offeredCpc) || 0;
  const budget = parseFloat(dailyBudget) || 0;

  // Determine resulting position when offering `offered`:
  // Position = (count of competitors with CPC ≥ offered) + 1
  const leadCalc = useMemo(() => {
    if (!cpc || offered <= 0) return null;
    const ranked = cpc.models.filter(m => m.pricePerClick != null) as (CpcCompetitor & { pricePerClick: number })[];
    const above = ranked.filter(m => m.pricePerClick > offered).length;
    const sameOrLower = ranked.filter(m => m.pricePerClick === offered);
    const expectedPosition = above + 1;
    const tier = cpc.tiers.find(t => expectedPosition >= t.positionFrom && expectedPosition <= t.positionTo);
    const tieCount = sameOrLower.length;

    /**
     * Forecast кликов в день при заявленном CPC.
     *
     * Стратегия (от точного к приблизительному):
     *  1) Если есть byPosition data для expectedPosition — берём её
     *     (наиболее точный сигнал: «эта позиция исторически даёт N
     *     кликов/день»). Требует чтобы Nuxt передавал ?pos= при клике.
     *  2) Если нет — берём avg по моделям ближайшего CPC tier (того
     *     tier'а, в который попадёт оффер). Опирается на pricePerClick
     *     models в ladder + clicksByUsername.
     *  3) Если и tier пуст (нет моделей в нём) — null, в UI покажем "—".
     *
     * Также считаем "tier above" — сколько кликов в среднем получают
     * модели выше по позиции, чтобы можно было сказать «обогнав tier
     * 0.78 ты получишь примерно как они».
     */
    let forecastClicksPerDay: number | null = null;
    let forecastSource: 'position' | 'tier' | null = null;
    let forecastLow: number | null = null;
    let forecastHigh: number | null = null;

    // Forecast — всегда last 24h (clicks24h), чтобы прогноз отражал
    // актуальное состояние ladder'а. Statistical CI (95%, Poisson approx):
    //   λ = clicks за 24h на этой позиции
    //   95% CI ≈ λ ± 1.96·√λ  (для λ>10 нормальная аппроксимация Пуассона)
    if (clicks24h && clicks24h.byPosition.length > 0) {
      const posRow = clicks24h.byPosition.find(p => p.position === expectedPosition);
      if (posRow && posRow.confirmed > 0) {
        const lambda = posRow.confirmed; // confirmed clicks/day (Poisson rate)
        const half = 1.96 * Math.sqrt(lambda);
        forecastClicksPerDay = lambda;
        forecastSource = 'position';
        forecastLow = Math.max(0, lambda - half);
        forecastHigh = lambda + half;
      }
    }

    // Fallback на tier-average — ищем ближайший существующий CPC tier
    // не выше оффера. CI: ±1.96·σ/√n (95% normal approx по моделям в tier'е).
    let tierAboveAvg: number | null = null;
    let tierAboveCpc: number | null = null;
    if (cpc.tiers.length > 0 && tierAggregates.size > 0) {
      const lowerCpcTier = cpc.tiers
        .filter(t => t.cpc != null && t.cpc <= offered)
        .reduce<typeof cpc.tiers[number] | null>((best, t) => {
          if (!best) return t;
          return (t.cpc! > best.cpc!) ? t : best;
        }, null);
      if (lowerCpcTier && lowerCpcTier.cpc != null) {
        const agg = tierAggregates.get(lowerCpcTier.cpc);
        if (agg) {
          tierAboveAvg = agg.avgClicksPerDay;
          tierAboveCpc = lowerCpcTier.cpc;
        }
      }
      if (forecastClicksPerDay == null && tierAboveAvg != null) {
        forecastClicksPerDay = tierAboveAvg;
        forecastSource = 'tier';
        // CI через stddev моделей в tier'е (см. tierAggregates).
        const lowerCpcTierCpc = lowerCpcTier?.cpc;
        const agg = lowerCpcTierCpc != null ? tierAggregates.get(lowerCpcTierCpc) : undefined;
        if (agg && agg.stdevClicksPerDay != null && agg.modelCount > 1) {
          const sem = agg.stdevClicksPerDay / Math.sqrt(agg.modelCount);
          forecastLow = Math.max(0, tierAboveAvg - 1.96 * sem);
          forecastHigh = tierAboveAvg + 1.96 * sem;
        } else {
          // Один модель в tier'е → нет вариативности для оценки. Fallback ±30%.
          forecastLow = tierAboveAvg * 0.7;
          forecastHigh = tierAboveAvg * 1.3;
        }
      }
    }

    return {
      position: expectedPosition,
      tier,
      tieCount,
      topSuggestion: cpc.stats.topBidSuggestion,
      beatNextTier: (() => {
        if (!tier) return null;
        const idx = cpc.tiers.indexOf(tier);
        const aboveTier = cpc.tiers[idx - 1];
        if (!aboveTier || aboveTier.cpc == null) return null;
        return aboveTier.cpc + 0.0010;
      })(),
      forecast: forecastClicksPerDay != null ? {
        clicksPerDay: forecastClicksPerDay,
        low: forecastLow,
        high: forecastHigh,
        source: forecastSource!,
        dailyCost: forecastClicksPerDay * offered,
        tierBenchmark: tierAboveAvg != null && tierAboveCpc != null
          ? { cpc: tierAboveCpc, avgClicksPerDay: tierAboveAvg }
          : null
      } : null
    };
  }, [cpc, offered, clicks24h, tierAggregates]);

  const composeQuote = () => {
    if (!cpc || !leadCalc || !selectedPage) return;
    const lines = [
      `OnlySeeker · CPC quote for ${pageDisplay(selectedPage)}`,
      `URL: ${pageLiveUrl(selectedPage)}`,
      ``,
      `Page status:`,
      `  • PPC competitors: ${cpc.stats.total} (${cpc.stats.withCpc} with CPC set)`,
      cpc.stats.maxCpc != null
        ? `  • Current CPC range: ${fmtCpc(cpc.stats.minCpc)} … ${fmtCpc(cpc.stats.maxCpc)}, median ${fmtCpc(cpc.stats.medianCpc)}`
        : `  • No active CPC competitors yet`,
      ``,
      `Your offer: ${fmtCpc(offered)} CPC`,
      `  → expected position #${leadCalc.position}` + (leadCalc.tier ? ` (tier ${leadCalc.tier.positionRange})` : ''),
      `  → budget cap: ${fmtCpc(budget)} = max ${budget > 0 && offered > 0 ? Math.floor(budget / offered) : 0} clicks/day`,
      leadCalc.forecast
        ? `  → forecast: ~${leadCalc.forecast.clicksPerDay.toFixed(1)} clicks/day (${leadCalc.forecast.low?.toFixed(1)}–${leadCalc.forecast.high?.toFixed(1)}), projected spend ${fmtCpc(leadCalc.forecast.dailyCost)}/day`
        : `  → forecast: not enough click data yet (last ${clicksDays}d)`,
      leadCalc.forecast?.tierBenchmark
        ? `  → comparable tier ${fmtCpc(leadCalc.forecast.tierBenchmark.cpc)}: models avg ${leadCalc.forecast.tierBenchmark.avgClicksPerDay.toFixed(1)} clicks/day`
        : ``,
      ladderChurn && ladderChurn.level !== 'none'
        ? `  ⚠ ladder churned ${Math.round(ladderChurn.ratio * 100)}% during this window — forecast is a rough estimate`
        : ``,
      ``,
      leadCalc.topSuggestion != null
        ? `To take position #1: bid ≥ ${fmtCpc(leadCalc.topSuggestion)} (current top is ${fmtCpc(cpc.stats.maxCpc)})`
        : ``,
      leadCalc.beatNextTier != null
        ? `To move up one tier: bid ≥ ${fmtCpc(leadCalc.beatNextTier)}`
        : ``
    ].filter(Boolean).join('\n');
    navigator.clipboard?.writeText(lines);
    alert('Quote copied to clipboard.');
  };

  // Bulk CSV export
  const exportAllCsv = async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      // Send in chunks of 200 pages per request (server limit 1000 but smaller chunks are friendlier)
      const CHUNK = 200;
      const allRows: import('../api/pins').CpcBulkRow[] = [];
      for (let i = 0; i < ALL_PAGES.length; i += CHUNK) {
        const chunk = ALL_PAGES.slice(i, i + CHUNK);
        const r = await getCpcStatsBulk(chunk);
        allRows.push(...r.pages);
      }
      // Build CSV
      const header = ['page', 'tag_count', 'total_models', 'pinned_models', 'with_cpc', 'min_cpc', 'max_cpc', 'median_cpc', 'avg_cpc', 'top1_username'];
      const rows: string[] = [header.join(',')];
      for (const row of allRows) {
        rows.push([
          '"' + row.tags.join('+') + '"',
          row.tags.length,
          row.totalModels,
          row.pinnedModels,
          row.withCpc,
          row.minCpc ?? '',
          row.maxCpc ?? '',
          row.medianCpc ?? '',
          row.avgCpc ?? '',
          row.top1Username ? '"' + row.top1Username + '"' : ''
        ].join(','));
      }
      const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpc-by-page-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('CSV export failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBulkBusy(false);
    }
  };

  const actions = (
    <>
      <button className="hf-btn" onClick={exportAllCsv} disabled={bulkBusy}>
        {bulkBusy ? <><Loader2 size={14} style={{ animation: 'hf-spin 1s linear infinite' }} /> Exporting…</> : <><Download size={14} /> Export all (CSV)</>}
      </button>
    </>
  );

  return (
    <>
      <PageHeader
        title="CPC"
        subtitle={<><span className="hf-mono">{ALL_PAGES.length.toLocaleString()}</span> pages · search to pick one</>}
        actions={actions}
      />

      <div className="hf-content">
        <SubTabs items={CPC_TABS} />
        {/* Page picker — collapses to a one-line summary once a page is chosen */}
        {selectedPage && !pickerEditing ? (
          <div className="hf-feature-box" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
            <span className="hf-label" style={{ margin: 0, flex: '0 0 auto' }}>Page</span>
            <span className="hf-mono" style={{ flex: '0 0 auto' }}>{pageDisplay(selectedPage)}</span>
            {cpc && (
              <span className="hf-help" style={{ marginTop: 0 }}>
                · {cpc.stats.total} models · {cpc.stats.withCpc} with CPC
              </span>
            )}
            <a
              href={pageLiveUrl(selectedPage)}
              target="_blank"
              rel="noreferrer"
              className="hf-mono"
              style={{ color: 'var(--accent)', fontSize: 11, marginLeft: 'auto' }}
            >
              {pageLiveUrl(selectedPage).replace('https://', '')} ↗
            </a>
            <button
              className="hf-btn ghost sm"
              type="button"
              onClick={() => { setPickerEditing(true); setShowDropdown(true); }}
            >
              Change
            </button>
          </div>
        ) : (
        <div className="hf-feature-box" ref={dropdownRef}>
          <div className="hf-label-row" style={{ marginBottom: 8 }}>
            <span className="hf-label">Pick a page</span>
            <span className="hf-help" style={{ marginTop: 0 }}>type tag(s) or paste URL/path</span>
          </div>
          <div className="hf-input" style={{ position: 'relative' }}>
            <Search size={14} />
            <input
              type="text"
              value={pageQuery}
              onChange={(e) => { setPageQuery(e.target.value); setShowDropdown(true); setActiveIdx(0); }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filteredPages.length - 1)); }
                if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const picked = filteredPages[activeIdx];
                  if (picked) {
                    setSelectedPage(picked);
                    setPageQuery(pageSlug(picked));
                    setShowDropdown(false);
                    setPickerEditing(false);
                  }
                }
                if (e.key === 'Escape') setShowDropdown(false);
              }}
              placeholder="bbw  or  bbw+german  or  /best/onlyfans-bbw"
            />
          </div>

          {selectedPage && (
            <div className="hf-help" style={{ marginTop: 6 }}>
              Selected: <span className="hf-mono">{pageDisplay(selectedPage)}</span>
              {' '}({selectedPage.length === 1 ? 'solo tag' : `${selectedPage.length}-tag combo`})
              {' · '}
              <a href={pageLiveUrl(selectedPage)} target="_blank" rel="noreferrer" className="hf-mono" style={{ color: 'var(--accent)' }}>
                {pageLiveUrl(selectedPage).replace('https://', '')} ↗
              </a>
            </div>
          )}

          {showDropdown && filteredPages.length > 0 && (
            <div style={{
              position: 'absolute',
              left: 16,
              right: 16,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-2)',
              marginTop: 4,
              boxShadow: 'var(--shadow-md)',
              maxHeight: 320,
              overflow: 'auto',
              zIndex: 10
            }}>
              {filteredPages.map((p, i) => (
                <button
                  key={p.length === 0 ? '__best__' : p.join('+')}
                  type="button"
                  onClick={() => { setSelectedPage(p); setPageQuery(pageSlug(p)); setShowDropdown(false); setPickerEditing(false); }}
                  style={{
                    display: 'flex',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    background: i === activeIdx ? 'var(--surface-2)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'var(--fg-1)',
                    fontSize: 13,
                    alignItems: 'center',
                    gap: 8
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className="hf-mono" style={{ flex: 1 }}>{pageSlug(p)}</span>
                  <span className="hf-tag outline sm">
                    {isBestOnly(p) ? 'best' : p.length === 1 ? 'solo' : `${p.length}-combo`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Empty state */}
        {!selectedPage && (
          <div className="hf-card">
            <div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)', padding: 30 }}>
              Pick a page above to see its CPC competitors and tier breakdown.
            </div>
          </div>
        )}

        {/* Loading — KPI shimmer + table shimmer */}
        {selectedPage && cpcLoading && (
          <>
            <div className="hf-kpi-strip">
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
              <SkelKpi />
            </div>
            <div className="hf-card flush">
              <table className="hf-table">
                <thead>
                  <tr>
                    <th>position</th>
                    <th>CPC</th>
                    <th className="num">models</th>
                    <th className="num">avg clicks/day</th>
                    <th>members</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <SkelRow key={i} cols={5} numCols={[2, 3]} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Error */}
        {selectedPage && cpcError && (
          <div className="hf-banner danger">
            <AlertCircle size={14} /> Failed to load CPC stats. <button className="hf-btn ghost sm" onClick={() => refetch()}>Retry</button>
          </div>
        )}

        {/* Data */}
        {selectedPage && cpc && (
          <>
            {/* KPI strip */}
            <div className="hf-kpi-strip">
              <div className="hf-kpi">
                <span className="hf-kpi-label">Total competitors</span>
                <span className="hf-kpi-value">{fmt(cpc.stats.total)}</span>
                <span className="hf-kpi-sub">{cpc.stats.pinned} pinned · {cpc.stats.total - cpc.stats.pinned} OT-eligible</span>
              </div>
              <div className="hf-kpi">
                <span className="hf-kpi-label">With CPC set</span>
                <span className="hf-kpi-value">{fmt(cpc.stats.withCpc)}</span>
                <span className="hf-kpi-sub">{cpc.stats.withoutCpc} without</span>
              </div>
              <div className="hf-kpi accent">
                <span className="hf-kpi-label">Max CPC (top)</span>
                <span className="hf-kpi-value">{fmtCpcShort(cpc.stats.maxCpc)}</span>
                <span className="hf-kpi-sub">to take #1: {fmtCpcShort(cpc.stats.topBidSuggestion)}</span>
              </div>
              <div className="hf-kpi">
                <span className="hf-kpi-label">Median CPC</span>
                <span className="hf-kpi-value">{fmtCpcShort(cpc.stats.medianCpc)}</span>
                <span className="hf-kpi-sub">avg {fmtCpcShort(cpc.stats.avgCpc)}</span>
              </div>
              <div className="hf-kpi">
                <span className="hf-kpi-label">Min CPC</span>
                <span className="hf-kpi-value">{fmtCpcShort(cpc.stats.minCpc)}</span>
              </div>
            </div>

            <div className="hf-two-col wide-right">
              {/* Tier ladder */}
              <div className="hf-card flush">
                <div className="hf-card-header">
                  <h3 className="hf-card-title">CPC tier ladder</h3>
                  <span className="hf-help" style={{ marginTop: 0 }}>
                    Models with identical CPC occupy consecutive positions.
                  </span>
                </div>
                {cpc.tiers.length === 0 ? (
                  <div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)', padding: 30 }}>
                    No competitors on this page yet.
                  </div>
                ) : (
                  <table className="hf-table">
                    <thead>
                      <tr>
                        <th>position</th>
                        <th>CPC</th>
                        <th className="num">models</th>
                        <th className="num">avg clicks/day</th>
                        <th>members</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cpc.tiers.map((t, i) => {
                        const isLead = leadCalc?.tier?.positionFrom === t.positionFrom && leadCalc?.tier?.positionTo === t.positionTo;
                        const agg = t.cpc != null ? tierAggregates.get(t.cpc) : undefined;
                        return (
                          <tr key={i} className={isLead ? 'selected' : ''}>
                            <td className="hf-mono" style={{ fontWeight: 600 }}>{t.positionRange}</td>
                            <td>
                              {t.cpc == null
                                ? <span className="hf-tag warning sm">no CPC</span>
                                : <span style={{ fontWeight: 600 }}>{fmtCpc(t.cpc)}</span>}
                            </td>
                            <td className="num">{t.count}</td>
                            <td className="num">
                              {agg
                                ? <>
                                    <span className="hf-mono">{agg.avgClicksPerDay.toFixed(1)}</span>
                                    <span style={{ color: 'var(--fg-3)', fontSize: 10, marginLeft: 4 }}>med {agg.medianClicksPerDay.toFixed(1)}</span>
                                  </>
                                : <span style={{ color: 'var(--fg-3)' }}>—</span>}
                            </td>
                            <td style={{ maxWidth: 280 }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {t.usernames.slice(0, 6).map(u => (
                                  <span key={u} className="hf-mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>@{u}</span>
                                ))}
                                {t.usernames.length > 6 && <span className="hf-help" style={{ marginTop: 0 }}>+{t.usernames.length - 6} more</span>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Lead calculator */}
              <div className="hf-card">
                <div className="hf-card-header"><h3 className="hf-card-title">Quote builder</h3></div>
                <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p className="hf-help" style={{ marginTop: 0 }}>
                    Partner offers <strong>$X CPC</strong>, what position do they get and at what daily volume?
                  </p>

                  <div>
                    <div className="hf-label-row"><span className="hf-label">Offered CPC</span></div>
                    <div className="hf-input">
                      <span className="hf-mono" style={{ color: 'var(--fg-3)' }}>$</span>
                      <input type="number" step="0.001" value={offeredCpc} onChange={(e) => setOfferedCpc(e.target.value)} />
                    </div>
                  </div>

                  <div>
                    <div className="hf-label-row"><span className="hf-label">Daily budget</span></div>
                    <div className="hf-input">
                      <span className="hf-mono" style={{ color: 'var(--fg-3)' }}>$</span>
                      <input type="number" step="1" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} />
                    </div>
                  </div>

                  <div className="hf-divider dashed" />

                  {leadCalc ? (
                    <>
                      <Row k="Resulting position" v={<span className="hf-mono" style={{ fontWeight: 600 }}>#{leadCalc.position}</span>} />
                      <Row k="Tier" v={leadCalc.tier
                        ? <span className="hf-tag accent sm">{leadCalc.tier.positionRange}</span>
                        : <span className="hf-help" style={{ marginTop: 0 }}>—</span>} />
                      <Row k="Max possible clicks/day" v={
                        offered > 0 && budget > 0
                          ? <span className="hf-mono">{Math.floor(budget / offered)}</span>
                          : '—'
                      } />

                      {leadCalc.forecast && (
                        <>
                          <div className="hf-divider dashed" />
                          <div style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                            Forecast · {clicksDays}d data
                          </div>
                          <Row
                            k="Expected clicks/day"
                            v={
                              <span className="hf-mono" style={{ fontWeight: 600, color: 'var(--accent)' }}>
                                ~{leadCalc.forecast.clicksPerDay.toFixed(1)}
                                {leadCalc.forecast.low != null && leadCalc.forecast.high != null && (
                                  <span style={{ fontWeight: 400, color: 'var(--fg-3)' }}>
                                    {' '}({leadCalc.forecast.low.toFixed(1)}–{leadCalc.forecast.high.toFixed(1)})
                                  </span>
                                )}
                              </span>
                            }
                          />
                          <Row
                            k="Projected daily spend"
                            v={<span className="hf-mono">{fmtCpcShort(leadCalc.forecast.dailyCost)}</span>}
                          />
                          {budget > 0 && (
                            <Row
                              k="Budget vs forecast"
                              v={
                                <span className="hf-mono" style={{ color: leadCalc.forecast.dailyCost > budget ? 'var(--danger)' : 'var(--fg-1)' }}>
                                  {leadCalc.forecast.dailyCost > budget
                                    ? `over by ${fmtCpcShort(leadCalc.forecast.dailyCost - budget)}`
                                    : `${Math.round((leadCalc.forecast.dailyCost / budget) * 100)}% used`}
                                </span>
                              }
                            />
                          )}
                          <div className="hf-help" style={{ marginTop: 4, fontSize: 11 }}>
                            Source:{' '}
                            {leadCalc.forecast.source === 'position'
                              ? <>position-level CTR · last 24h, Poisson 95% CI</>
                              : <>tier average · last 24h, normal 95% CI by σ/√n</>}
                            {leadCalc.forecast.tierBenchmark && (
                              <>
                                <br />
                                Comparable tier {fmtCpc(leadCalc.forecast.tierBenchmark.cpc)}:
                                models avg <span className="hf-mono">{leadCalc.forecast.tierBenchmark.avgClicksPerDay.toFixed(1)}</span> clicks/day.
                              </>
                            )}
                          </div>

                          {ladderChurn && ladderChurn.level !== 'none' && (
                            <div className="hf-banner warning" style={{ marginTop: 8, padding: '8px 10px', fontSize: 11 }}>
                              <AlertCircle size={12} />
                              <span>
                                <strong>Ladder churned during {clicksDays}d window</strong>
                                {' '}({ladderChurn.missingCount} dropped, {ladderChurn.newCount} added — {Math.round(ladderChurn.ratio * 100)}% turnover).
                                {ladderChurn.level === 'high' && <> Forecast accuracy reduced — try <strong>1d</strong> or <strong>3d</strong> window for fresh state.</>}
                                {ladderChurn.missingTop.length > 0 && (
                                  <> Gone: {ladderChurn.missingTop.map(u => <span key={u} className="hf-mono">@{u}</span>).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}.</>
                                )}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      <div className="hf-divider dashed" />
                      <div className="hf-help" style={{ marginTop: 0 }}>
                        {leadCalc.topSuggestion != null && offered < (cpc.stats.maxCpc ?? Infinity) && (
                          <>To take <strong>#1</strong>: bid ≥ <span className="hf-mono">{fmtCpc(leadCalc.topSuggestion)}</span><br /></>
                        )}
                        {leadCalc.beatNextTier != null && (
                          <>To move up one tier: bid ≥ <span className="hf-mono">{fmtCpc(leadCalc.beatNextTier)}</span></>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="hf-help" style={{ marginTop: 0 }}>Enter an offered CPC above.</div>
                  )}

                  <div className="hf-divider dashed" />
                  <button className="hf-btn primary" onClick={composeQuote} disabled={!leadCalc}>
                    <Copy size={14} /> Quote → clipboard
                  </button>
                </div>
              </div>
            </div>

            {/* Tracking v2 — фактические клики по странице за период */}
            <div className="hf-card" style={{ marginBottom: 16 }}>
              <div className="hf-card-header">
                <h3 className="hf-card-title">Page traffic (tracking v2)</h3>
                <div className="hf-segmented">
                  {CLICK_RANGES.map(r => (
                    <button key={r.value} className={clicksDays === r.value ? 'on' : ''} onClick={() => setClicksDays(r.value)}>{r.label}</button>
                  ))}
                </div>
              </div>
              {!clicks ? (
                <div className="hf-card-body" style={{ color: 'var(--fg-3)' }}>Loading clicks…</div>
              ) : clicks.summary.total === 0 ? (
                <div className="hf-card-body" style={{ color: 'var(--fg-3)' }}>
                  No clicks on this page in last {clicks.days}d.
                  Make sure Nuxt is forwarding <code>?p={pageKey}</code> and click_events are accumulating.
                </div>
              ) : (
                <>
                  <div className="hf-kpi-strip" style={{ padding: '0 16px 12px' }}>
                    <div className="hf-kpi accent">
                      <span className="hf-kpi-label">Clicks · {clicks.days}d</span>
                      <span className="hf-kpi-value">{fmt(clicks.summary.total)}</span>
                      <span className="hf-kpi-sub">redirected+confirmed+bounced</span>
                    </div>
                    <div className="hf-kpi">
                      <span className="hf-kpi-label">Confirmed</span>
                      <span className="hf-kpi-value">{fmt(clicks.summary.confirmed)}</span>
                      <span className="hf-kpi-sub">CTR {fmtPct(clicks.summary.ctr)}</span>
                    </div>
                    <div className="hf-kpi">
                      <span className="hf-kpi-label">Bounced</span>
                      <span className="hf-kpi-value">{fmt(clicks.summary.bounced)}</span>
                      <span className="hf-kpi-sub">{fmtPct(clicks.summary.bounceRate)} bounce</span>
                    </div>
                    <div className="hf-kpi">
                      <span className="hf-kpi-label">Bots / Dup</span>
                      <span className="hf-kpi-value">{fmt(clicks.summary.bot + clicks.summary.duplicate)}</span>
                      <span className="hf-kpi-sub">excluded from CTR</span>
                    </div>
                  </div>

                  {clicks.byPosition.length > 0 && (
                    <table className="hf-table">
                      <thead>
                        <tr>
                          <th>pos</th>
                          <th className="num">clicks</th>
                          <th className="num">confirmed</th>
                          <th className="num">bounced</th>
                          <th className="num">CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clicks.byPosition.map(p => (
                          <tr key={p.position}>
                            <td className="hf-mono">#{p.position}</td>
                            <td className="num">{fmt(p.total)}</td>
                            <td className="num">{fmt(p.confirmed)}</td>
                            <td className="num">{fmt(p.bounced)}</td>
                            <td className="num">{fmtPct(p.ctr)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>

            {/* All competitors table — CPC ladder + click-merge */}
            <div className="hf-card flush">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Full ranking · CPC + clicks {clicksDays}d</h3>
                <span className="hf-help" style={{ marginTop: 0 }}>
                  {cpc.models.length} models · sorted by CPC desc (no-CPC at bottom)
                </span>
              </div>
              <table className="hf-table">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>@username</th>
                    <th>name</th>
                    <th>cpc</th>
                    <th className="num">clicks</th>
                    <th className="num">confirmed</th>
                    <th className="num">CTR</th>
                    <th>type</th>
                    <th>src</th>
                    <th>pinned?</th>
                  </tr>
                </thead>
                <tbody>
                  {cpc.models.map(m => {
                    const ck = clicksByUsername.get(m.username.toLowerCase());
                    return (
                      <tr key={m.username}>
                        <td className="hf-mono" style={{ color: 'var(--fg-3)' }}>{m.position}</td>
                        <td><span className="hf-mono">@{m.username}</span></td>
                        <td style={{ color: 'var(--fg-2)' }}>{m.modelName ?? '—'}</td>
                        <td className="hf-mono">{m.pricePerClick != null ? fmtCpc(m.pricePerClick) : <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td className="num">{ck ? fmt(ck.total) : <span style={{ color: 'var(--fg-3)' }}>0</span>}</td>
                        <td className="num">{ck ? fmt(ck.confirmed) : <span style={{ color: 'var(--fg-3)' }}>0</span>}</td>
                        <td className="num">{ck && ck.total > 0 ? fmtPct(ck.ctr) : <span style={{ color: 'var(--fg-3)' }}>—</span>}</td>
                        <td>{m.ppcType && <span className="hf-tag outline sm">{m.ppcType}</span>}</td>
                        <td><span className="hf-tag outline sm">{m.source ?? '—'}</span></td>
                        <td>{m.pinned ? <span className="hf-tag accent sm">pinned</span> : <span className="hf-tag outline sm">OT-eligible</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
    <span style={{ color: 'var(--fg-3)' }}>{k}</span>
    <span style={{ color: 'var(--fg-1)' }}>{v}</span>
  </div>
);

export default CpcByPagePage;
